import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import './Modal.css';

const PERMISSION_LABELS = [
  { key: 'view_stamp',   label: 'View Stamps'   },
  { key: 'add_stamp',    label: 'Add Stamp'      },
  { key: 'edit_stamp',   label: 'Edit Stamp'     },
  { key: 'delete_stamp', label: 'Delete Stamp'   },
  { key: 'bulk_import',  label: 'Bulk Import'    },
];

const DEFAULT_PERMISSIONS = {
  view_stamp: true, add_stamp: false,
  edit_stamp: false, delete_stamp: false, bulk_import: false,
};

function ManageAdminsModal({ onClose }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ username: '', password: '', role: 'admin' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset password panel
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Permissions panel
  const [permTarget, setPermTarget] = useState(null); // { id, username, permissions }
  const [permState, setPermState] = useState({ ...DEFAULT_PERMISSIONS });
  const [permLoading, setPermLoading] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'superadmin';

  useEffect(() => { fetchAdmins(); }, []);

  const fetchAdmins = async () => {
    try {
      const res = await authAPI.getAdmins();
      setAdmins(res.data.users);
    } catch {
      toast.error('Failed to load admin users');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.addAdmin(form);
      toast.success(`Admin "${form.username}" created`);
      setForm({ username: '', password: '', role: 'admin' });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete admin "${username}"?`)) return;
    try {
      await authAPI.deleteAdmin(id);
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete admin');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPw || resetPw.length < 6)
      return toast.error('Password must be at least 6 characters');
    setResetLoading(true);
    try {
      await authAPI.resetAdminPassword(resetTarget.id, resetPw);
      toast.success(`Password reset for "${resetTarget.username}"`);
      setResetTarget(null);
      setResetPw('');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const openPermissions = (admin) => {
    setPermTarget({ id: admin.id, username: admin.username });
    setPermState({ ...DEFAULT_PERMISSIONS, ...(admin.permissions || {}) });
    setResetTarget(null);
  };

  const handlePermSave = async (e) => {
    e.preventDefault();
    setPermLoading(true);
    try {
      await authAPI.updatePermissions(permTarget.id, permState);
      toast.success(`Permissions updated for "${permTarget.username}"`);
      setPermTarget(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update permissions');
    } finally {
      setPermLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box modal-box-wide">
        <div className="modal-box-header">
          <div className="modal-box-title">
            <AdminPanelSettingsIcon className="modal-title-icon" />
            <span>Manage Admin Users</span>
          </div>
          <button className="icon-btn" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="modal-box-body">

          {/* ── Add new admin (superadmin only) ── */}
          {isSuperAdmin && (
            <div className="admin-add-section">
              <h4 className="section-subtitle"><PersonAddIcon fontSize="small" /> Add New Admin</h4>
              <form onSubmit={handleAdd} className="admin-add-form">
                <div className="form-group">
                  <label>Username</label>
                  <input type="text" value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="Enter username" required />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <div className="input-with-icon">
                    <input type={showPass ? 'text' : 'password'} value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Min 6 characters" required />
                    <button type="button" className="input-icon-btn" onClick={() => setShowPass(!showPass)}>
                      {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Admin'}
                </button>
              </form>
            </div>
          )}

          {/* ── Reset password panel ── */}
          {resetTarget && (
            <div className="reset-pw-panel">
              <h4 className="section-subtitle">
                <LockResetIcon fontSize="small" /> Reset password for <b>{resetTarget.username}</b>
              </h4>
              <form onSubmit={handleResetPassword} className="reset-pw-form">
                <div className="form-group">
                  <label>New Password</label>
                  <div className="input-with-icon">
                    <input type={showResetPw ? 'text' : 'password'} value={resetPw}
                      onChange={(e) => setResetPw(e.target.value)}
                      placeholder="Min 6 characters" required />
                    <button type="button" className="input-icon-btn" onClick={() => setShowResetPw(!showResetPw)}>
                      {showResetPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </button>
                  </div>
                </div>
                <div className="modal-box-actions" style={{ marginTop: 8 }}>
                  <button type="submit" className="btn btn-primary" disabled={resetLoading}>
                    {resetLoading ? 'Saving...' : 'Set Password'}
                  </button>
                  <button type="button" className="btn btn-secondary"
                    onClick={() => { setResetTarget(null); setResetPw(''); }}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Permissions panel ── */}
          {permTarget && (
            <div className="perm-panel">
              <h4 className="section-subtitle">
                <TuneIcon fontSize="small" /> Permissions for <b>{permTarget.username}</b>
              </h4>
              <form onSubmit={handlePermSave}>
                <div className="perm-checkboxes">
                  {PERMISSION_LABELS.map(({ key, label }) => (
                    <label key={key} className="perm-checkbox-row">
                      <input
                        type="checkbox"
                        checked={!!permState[key]}
                        onChange={(e) => setPermState({ ...permState, [key]: e.target.checked })}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <div className="modal-box-actions" style={{ marginTop: 12 }}>
                  <button type="submit" className="btn btn-primary" disabled={permLoading}>
                    {permLoading ? 'Saving...' : 'Save Permissions'}
                  </button>
                  <button type="button" className="btn btn-secondary"
                    onClick={() => setPermTarget(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Existing admins list ── */}
          <div className="admin-list-section">
            <h4 className="section-subtitle">
              {isSuperAdmin ? `All Admin Users (${admins.length})` : 'Your Account'}
            </h4>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th>Pw Last Changed</th>
                    {isSuperAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((a) => (
                    <tr key={a.id} className={String(a.id) === String(currentUser.id) ? 'current-user-row' : ''}>
                      <td>
                        {a.username}
                        {String(a.id) === String(currentUser.id) && <span className="you-badge">You</span>}
                      </td>
                      <td><span className={`role-badge role-${a.role}`}>{a.role}</span></td>
                      <td>
                        {a.role === 'superadmin' ? (
                          <span className="perm-all">All access</span>
                        ) : (
                          <div className="perm-tags">
                            {PERMISSION_LABELS.filter(({ key }) => a.permissions?.[key])
                              .map(({ key, label }) => (
                                <span key={key} className="perm-tag">{label}</span>
                              ))}
                            {!PERMISSION_LABELS.some(({ key }) => a.permissions?.[key]) && (
                              <span className="text-muted">None</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {a.password_changed_at
                          ? <span className="pw-changed">{new Date(a.password_changed_at).toLocaleString()}</span>
                          : <span className="text-muted">Never</span>}
                      </td>
                      {isSuperAdmin && (
                        <td>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {String(a.id) !== String(currentUser.id) && a.role !== 'superadmin' && (
                              <button className="icon-btn" title="Edit permissions"
                                onClick={() => openPermissions(a)}>
                                <TuneIcon fontSize="small" style={{ color: '#3498db' }} />
                              </button>
                            )}
                            {String(a.id) !== String(currentUser.id) && (
                              <button className="icon-btn" title="Reset password"
                                onClick={() => { setResetTarget({ id: a.id, username: a.username }); setResetPw(''); setPermTarget(null); }}>
                                <LockResetIcon fontSize="small" style={{ color: '#f39c12' }} />
                              </button>
                            )}
                            {String(a.id) !== String(currentUser.id) && a.role !== 'superadmin' && (
                              <button className="icon-btn icon-btn-danger" title="Delete admin"
                                onClick={() => handleDelete(a.id, a.username)}>
                                <DeleteIcon fontSize="small" />
                              </button>
                            )}
                            {String(a.id) === String(currentUser.id) && (
                              <span className="text-muted">—</span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ManageAdminsModal;
